import { useCallback } from 'react';
import { Assessment, Category } from '@/types/assessment';
import { getAnswerRecommendations } from '@/utils/recommendations';
import { sortRecommendationsByPriority } from '@/utils/recommendations';

interface UseRecommendationsProps {
  assessment?: Assessment;
  categories?: Category[];
  scoresByCategory?: Record<string, {current: number, target: number}>;
  responses?: Record<string, {
    questionId: string;
    current: string[] | number;
    target: string[] | number;
  }>;
  assessmentVersion?: number;
}

export function useRecommendations({ 
  assessment, 
  categories, 
  responses,
  assessmentVersion
}: UseRecommendationsProps) {
  
  // Method to collect recommendations from answers only
  const collectRecommendationsFromAnswers = useCallback(() => {
    console.log('\n🚀 [useRecommendations] Starting answer-specific recommendation collection...');
    
    if (!assessment || !categories) {
      console.log('❌ Missing assessment or categories');
      return [];
    }
    
    console.log('📊 Current state:', { 
      assessmentId: assessment.id, 
      categoriesCount: categories.length,
      hasResponses: !!responses && Object.keys(responses || {}).length > 0,
      responses: responses
    });

    // Use appropriate version of categories based on assessmentVersion
    let categoriesForVersion = categories;
    
    if (
      assessmentVersion !== undefined && 
      assessment.versionHistory && 
      assessmentVersion < assessment.version
    ) {
      const versionEntry = assessment.versionHistory.find(
        v => v.versionNumber === assessmentVersion
      );
      
      if (versionEntry) {
        categoriesForVersion = versionEntry.categories;
        console.log('📅 Using historical categories for version:', assessmentVersion);
      }
    }
    
    // Only try answer-specific recommendations
    if (responses && Object.keys(responses).length > 0) {
      console.log('🎯 Generating answer-specific recommendations');
      const answerRecommendations = getAnswerRecommendations(categoriesForVersion, responses);
      console.log(`✅ Found ${answerRecommendations.length} answer recommendations`);
      
      if (answerRecommendations.length > 0) {
        console.log('🎉 Using answer-specific recommendations');
        return sortRecommendationsByPriority(answerRecommendations);
      }
    }
    
    console.log('❌ No answer-specific recommendations could be generated');
    return [];
  }, [assessment, categories, responses, assessmentVersion]);

  // Keep generateRecommendations for backward compatibility
  const generateRecommendations = useCallback(() => {
    return collectRecommendationsFromAnswers();
  }, [collectRecommendationsFromAnswers]);

  return {
    collectRecommendationsFromAnswers,
    generateRecommendations
  };
}
